import { useSelector } from "react-redux";
import useLabels from "../../hooks/useLabels";
import useAuth from "../../hooks/useAuth";
import { ROLES } from "../../constants/base";
import { selectActiveWinery, selectUser } from "../../context/selectors";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Modal } from "@wine2trace/ui/components";

import "./styles/index.scss";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { WineriesSelector } from "../WineriesList";
import { getItem } from "../../services/LocalStorage";
import { isRedirectFromValidForWinery } from "../../services/redirect";
import { SkeletonNextOperations } from "../Skeleton/Skeleton";
import useDashboardSummary from "../../hooks/useDashboardSummary";
import HomeAttentionPanel from "./HomeAttentionPanel";
import HomeCellarStatus from "./HomeCellarStatus";
import HomeQuickActions from "./HomeQuickActions";
import HomeRecentAnalysis from "./HomeRecentAnalysis";
import { getGreetingKey } from "./greeting";
import HomeKpis from "./HomeKpis";

const NextOperations = lazy(() => import("./NextOperations"));

const TenantHome = () => {
  const [getLabel] = useLabels();
  const user = useSelector(selectUser);
  const ref = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const activeWinery = useSelector(selectActiveWinery);
  const [isOpen, setIsOpen] = useState(true);
  const { summary, status: summaryStatus, reload: reloadSummary } = useDashboardSummary();

  useEffect(() => {
    if (location.hash.includes("wineriesList") && !ref?.current?.open) {
      ref?.current?.showModal();
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!location.hash.includes("wineriesList") && ref?.current?.open) {
      ref?.current?.close();
      setIsOpen(false);
    }

    if (activeWinery === null) {
      ref?.current?.close();
      ref?.current?.showModal();
      setIsOpen(true);
    }

  }, [location.hash]);

  const winerySelectionCallback = (selectedWinery) => {
    // "from" arriva da Login (query token scaduto/logout): torna a quella pagina
    // solo se appartiene alla winery appena selezionata, altrimenti resta in Home
    // (l'URL potrebbe riferirsi a un tank/lotto/operazione di un'altra cantina).
    const from = location.state?.from;
    navigate(
      from && isRedirectFromValidForWinery(from, selectedWinery?.id)
        ? from.pathname
        : location.pathname
    );
    if (ref?.current?.open) {
      ref?.current?.close();
      setIsOpen(false);
    }
  };

  const renderWelcome = () => {
    const isMonitor = user?.type === "TANK";
    return (
      <div className="home_header">
        <h2>
          {isMonitor
            ? getItem("currentMonitor")
              ? `${getLabel(user?.type)} ${getItem("currentMonitor")}`
              : ""
            : getLabel(getGreetingKey(), { username: user?.userName })}
        </h2>
      </div>
    );
  };

  const renderReminder = () => {
    return (
      <section className="home_panel home_main_operations" aria-label={getLabel("nextOperations")}>
        <h4 className="home_panel_title">{getLabel("nextOperations")}</h4>
        <Suspense fallback={<SkeletonNextOperations rowsCount={5} />}>
          <NextOperations />
        </Suspense>
      </section>
    );
  };

  return (
    <section className="primary_container">
      <div className="primary_container_wrapper">
        {renderWelcome()}
        <HomeKpis summary={summary} status={summaryStatus} onRetry={reloadSummary} />
        <div className="home_main">
          {renderReminder()}
          <HomeAttentionPanel summary={summary} status={summaryStatus} onRetry={reloadSummary} />
        </div>
        <div className="home_main">
          <HomeCellarStatus summary={summary} status={summaryStatus} onRetry={reloadSummary} />
          <HomeQuickActions />
        </div>
        <HomeRecentAnalysis />
      </div>
      <Modal forwardRef={ref} customClassName={'is_winery_selector'} isOpen={isOpen} modalType={"login"}>
        <h4>{getLabel("wineriesModalSelector")}</h4>
        <WineriesSelector eventCallback={winerySelectionCallback} />
      </Modal>
    </section>
  );
};

// The platform ADMIN has no wineries and no tenant data (it gets there only by impersonation, where the
// token carries the target's role): it goes to /clients before any tenant request or the winery modal.
const Home = () => {
  const { auth } = useAuth();
  if (Number(auth?.role) === ROLES.ADMIN) return <Navigate to="/clients" replace />;
  return <TenantHome />;
};

export default Home;
